import { Edit, useForm, useSelect } from "@refinedev/antd";
import { HttpError, useCreate, useList, useUpdate } from "@refinedev/core";
import { Form, Input, Select, Row, Col, Card, App, Divider, Button, Modal, Space, Collapse } from "antd";
import { PlusOutlined, ArrowLeftOutlined, MailOutlined, DeleteOutlined, UserOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";
import { usePageTitle } from "../../hooks";
import { ContactFormList, PHONE_PREFIXES } from "./components/ContactFormList";
import { useState } from "react";

/** Parse a stored phone like "+34 600000000" into { prefix, number }. */
const parsePhone = (raw: string | null | undefined): { prefix: string; number: string } => {
  if (!raw) return { prefix: "+34", number: "" };
  // Sort prefixes longest-first so "+598" matches before "+5"
  const sorted = PHONE_PREFIXES.map((p) => p.value).sort((a, b) => b.length - a.length);
  for (const pfx of sorted) {
    if (raw.startsWith(pfx)) {
      return { prefix: pfx, number: raw.slice(pfx.length).trim() };
    }
  }
  return { prefix: "+34", number: raw };
};

/**
 * ProducerEdit Component
 * Form pre-loaded with current producer data for editing.
 */
export const ProducerEdit = () => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const navigate = useNavigate();
  const location = useLocation();

  usePageTitle(t("producers.actions.edit", "Editar Productor"));

  // Resolvemos a dónde volver DESPUÉS de guardar/cancelar. La regla es:
  //   1. Si venimos del detalle (`fromShow`), volvemos al detalle y
  //      arrastramos `fromList` para que el detalle sepa cómo volver
  //      al listado con el filtro.
  //   2. Si venimos del listado con filtros (`fromList` presente,
  //      incluido "" para el listado sin filtros), volvemos al listado
  //      con esos search params.
  //   3. Fallback: listado limpio.
  const state = location.state as any;
  const fromShow = !!state?.fromShow;
  const fromListSearch: string | undefined = state?.fromList;

  const { formProps, form, id } = useForm<any, HttpError, any>({
    action: "edit",
    resource: "producers",
    redirect: false,
    onMutationSuccess: () => {
      message.success(t("producers.messages.updated_success", "Productor actualizado correctamente"));
      goBackToOrigin();
    },
    onMutationError: (error: any) => {
      const resp = error?.response?.data || error?.data;
      if (resp?.cif) {
        const cifMsg = Array.isArray(resp.cif) ? resp.cif[0] : resp.cif;
        message.error(cifMsg);
        form.setFields([{ name: "cif", errors: [cifMsg] }]);
      } else {
        message.error(t("producers.messages.update_error", "Error al actualizar el productor"));
      }
    },
  });

  const goBackToOrigin = () => {
    if (fromShow && id) {
      // Volver al detalle del productor conservando fromList por si
      // luego el usuario pulsa "Volver" desde el detalle → listado.
      navigate(`/producers/show/${id}`, {
        state: { fromList: fromListSearch },
      });
      return;
    }
    if (fromListSearch !== undefined) {
      navigate(`/producers${fromListSearch}`);
      return;
    }
    goBackToOrigin();
  };

  const { mutate: createContact } = useCreate();
  const { mutate: updateContact } = useUpdate();

  // Fetch existing contacts for this producer
  const contactsQuery = useList<any>({
    resource: `producers/${id}/contacts`,
    pagination: { pageSize: 100 },
    queryOptions: { enabled: !!id },
  });
  const existingContacts = contactsQuery.result?.data ?? [];

  const { selectProps: portfolioSelectProps } = useSelect({
    resource: "portfolios",
    optionLabel: "name",
    optionValue: "id",
    pagination: { pageSize: 100 },
  });

  const { selectProps: agentSelectProps, query: agentQuery } = useSelect({
    resource: "agents",
    optionLabel: "name",
    optionValue: "id",
    pagination: { pageSize: 100 },
  });

  const { selectProps: businessGroupSelectProps, query: bgQuery } = useSelect({
    resource: "business-groups",
    optionLabel: "name",
    optionValue: "id",
    pagination: { pageSize: 100 },
  });

  // Ojo: NO usar el resource "countries" para el pais del productor
  // (mismo motivo que en create.tsx — divergencia entre geo.Country
  // y el enum COUNTRIES). Usamos /producer-countries/.
  const { selectProps: countrySelectProps } = useSelect({
    resource: "producer-countries",
    optionLabel: "name",
    optionValue: "id",
    pagination: { pageSize: 100 },
  });

  const { selectProps: positionSelectProps } = useSelect({
    resource: "business-positions",
    optionLabel: "name",
    optionValue: "id",
    pagination: { pageSize: 100 },
  });

  // Track which existing contacts to delete
  const [deletedContactIds, setDeletedContactIds] = useState<string[]>([]);

  // Inline creation state
  const [agentModalOpen, setAgentModalOpen] = useState(false);
  const [bgModalOpen, setBgModalOpen] = useState(false);
  const [agentForm] = Form.useForm();
  const [bgForm] = Form.useForm();

  const { mutate: createAgent } = useCreate();
  const { mutate: createBg } = useCreate();

  const handleCreateAgent = () => {
    agentForm.validateFields().then((values) => {
      createAgent(
        { resource: "agents", values },
        {
          onSuccess: (data) => {
            const newAgent = data.data as any;
            message.success(t("producers.messages.agent_created", "Responsable creado"));
            agentQuery.refetch();
            form.setFieldsValue({ agent: newAgent.id });
            setAgentModalOpen(false);
            agentForm.resetFields();
          },
        },
      );
    });
  };

  const handleCreateBg = () => {
    bgForm.validateFields().then((values) => {
      createBg(
        { resource: "business-groups", values },
        {
          onSuccess: (data) => {
            const newBg = data.data as any;
            message.success(t("producers.messages.bg_created", "Grupo empresarial creado"));
            bgQuery.refetch();
            form.setFieldsValue({ business_group: newBg.id });
            setBgModalOpen(false);
            bgForm.resetFields();
          },
        },
      );
    });
  };

  const selectFilter = (input: string, option: any) =>
    String(option?.label ?? "").toLowerCase().includes(input.toLowerCase());

  const handleSubmit = async (values: any) => {
    const { contacts, existing_contacts, ...producerValues } = values;

    // Sanitize CIF
    if (producerValues.cif) {
      producerValues.cif = producerValues.cif.replace(/[^A-Za-z0-9\-]/g, "");
    }

    // Submit producer update via formProps.onFinish
    await formProps.onFinish?.(producerValues);

    // Update existing contacts via PATCH
    const visibleContacts = (existingContacts || []).filter(
      (pc: any) => !deletedContactIds.includes(pc.id),
    );
    for (let i = 0; i < visibleContacts.length; i++) {
      const pc = visibleContacts[i];
      const edited = existing_contacts?.[i];
      if (!edited) continue;
      updateContact({
        resource: "producer-contacts",
        id: pc.id,
        values: {
          name: edited.name,
          surname: edited.surname,
          email: edited.email || null,
          main_phone: edited.main_phone
            ? `${edited.main_phone_prefix || "+34"} ${edited.main_phone}`
            : null,
          secondary_phone: edited.secondary_phone
            ? `${edited.secondary_phone_prefix || "+34"} ${edited.secondary_phone}`
            : null,
          identification_number: edited.identification_number || null,
          position_id: edited.position_id || null,
        },
      });
    }

    // Submit new contacts separately
    const newContacts = (contacts || []).filter((c: any) => c?.name);
    for (const contact of newContacts) {
      createContact({
        resource: `producers/${id}/contacts`,
        values: {
          name: contact.name,
          surname: contact.surname,
          email: contact.email || null,
          main_phone: contact.main_phone
            ? `${contact.phone_prefix || "+34"} ${contact.main_phone}`
            : null,
          secondary_phone: contact.secondary_phone
            ? `${contact.phone_prefix || "+34"} ${contact.secondary_phone}`
            : null,
          identification_number: contact.identification_number || null,
          position_id: contact.position_id || null,
        },
      });
    }
  };

  const addNewDropdown = (menu: React.ReactNode, onClick: () => void, label: string) => (
    <>
      {menu}
      <Divider style={{ margin: "8px 0" }} />
      <Button type="link" icon={<PlusOutlined />} onClick={onClick} block>
        {label}
      </Button>
    </>
  );

  return (
    <Edit
      title={t("producers.actions.edit", "Editar Productor")}
      saveButtonProps={{ onClick: () => form.submit() }}
      headerButtons={
        <Button icon={<ArrowLeftOutlined />} onClick={() => goBackToOrigin()}>
          {t("common.back", "Volver")}
        </Button>
      }
      footerButtons={
        <Space>
          <Button onClick={() => goBackToOrigin()}>
            {t("common.cancel", "Cancelar")}
          </Button>
          <Button type="primary" onClick={() => form.submit()}>
            {t("common.save", "Guardar")}
          </Button>
        </Space>
      }
    >
      <Form
        {...formProps}
        initialValues={{ ...formProps.initialValues, contacts: undefined, farms: undefined }}
        layout="vertical"
        onFinish={handleSubmit}
      >
        <Card>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                label={t("producers.fields.name", "Nombre")}
                name="name"
                rules={[{ required: true, message: t("producers.messages.name_required", "El nombre es obligatorio") }]}
              >
                <Input placeholder={t("producers.placeholders.name", "Nombre del productor")} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label={t("producers.fields.cif", "CIF / RUC")} name="cif">
                <Input placeholder={t("producers.placeholders.cif", "Ej: B12345678")} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Form.Item label={t("producers.fields.portfolio", "Cartera")} name="portfolio">
                <Select
                  {...portfolioSelectProps}
                  placeholder={t("producers.placeholders.portfolio", "Seleccionar cartera")}
                  allowClear
                  showSearch
                  filterOption={selectFilter}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item label={t("producers.fields.agent", "Responsable")} name="agent">
                <Select
                  {...agentSelectProps}
                  placeholder={t("producers.placeholders.agent", "Seleccionar responsable")}
                  allowClear
                  showSearch
                  filterOption={selectFilter}
                  dropdownRender={(menu) =>
                    addNewDropdown(menu, () => setAgentModalOpen(true), t("producers.actions.new_agent", "Nuevo responsable"))
                  }
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item label={t("producers.fields.country", "País")} name="country">
                <Select
                  {...countrySelectProps}
                  placeholder={t("producers.placeholders.country", "Seleccionar país")}
                  allowClear
                  showSearch
                  filterOption={selectFilter}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Form.Item label={t("producers.fields.business_group", "Grupo Empresarial")} name="business_group">
                <Select
                  {...businessGroupSelectProps}
                  placeholder={t("producers.placeholders.business_group", "Seleccionar grupo empresarial")}
                  allowClear
                  showSearch
                  filterOption={selectFilter}
                  dropdownRender={(menu) =>
                    addNewDropdown(menu, () => setBgModalOpen(true), t("producers.actions.new_business_group", "Nuevo grupo empresarial"))
                  }
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card title={t("contacts.title", "Contactos del Productor")} style={{ marginTop: 24 }}>
          {existingContacts.filter((pc: any) => !deletedContactIds.includes(pc.id)).length > 0 && (
            <Collapse
              defaultActiveKey={[]}
              style={{ marginBottom: 16 }}
              items={existingContacts
                .filter((pc: any) => !deletedContactIds.includes(pc.id))
                .map((pc: any, idx: number) => {
                  const c = pc.contact || {};
                  const mainPhone = parsePhone(c.main_phone);
                  const secPhone = parsePhone(c.secondary_phone);
                  const prefixFilter = (input: string, option: any) =>
                    String(option?.label ?? "").toLowerCase().includes(input.toLowerCase()) ||
                    String(option?.value ?? "").includes(input);
                  return {
                    key: pc.id || idx,
                    label: (
                      <Space>
                        <UserOutlined />
                        <span>{c.name} {c.surname}</span>
                      </Space>
                    ),
                    extra: (
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeletedContactIds((prev) => [...prev, pc.id]);
                        }}
                      />
                    ),
                    children: (
                      <>
                        <Row gutter={16}>
                          <Col xs={24} sm={12}>
                            <Form.Item
                              name={["existing_contacts", idx, "name"]}
                              label={t("contacts.field.name", "Nombre")}
                              initialValue={c.name}
                              rules={[{ required: true, message: t("contacts.error.required", "Campo requerido") }]}
                            >
                              <Input />
                            </Form.Item>
                          </Col>
                          <Col xs={24} sm={12}>
                            <Form.Item
                              name={["existing_contacts", idx, "surname"]}
                              label={t("contacts.field.surname", "Apellidos")}
                              initialValue={c.surname}
                              rules={[{ required: true, message: t("contacts.error.required", "Campo requerido") }]}
                            >
                              <Input />
                            </Form.Item>
                          </Col>
                        </Row>
                        <Row gutter={16}>
                          <Col xs={24} sm={12}>
                            <Form.Item
                              name={["existing_contacts", idx, "email"]}
                              label={t("contacts.field.email", "Email")}
                              initialValue={c.email}
                              rules={[{ type: "email", message: t("contacts.error.invalid_email", "Formato de email inválido") }]}
                            >
                              <Input prefix={<MailOutlined style={{ color: "#bfbfbf" }} />} />
                            </Form.Item>
                          </Col>
                          <Col xs={24} sm={12}>
                            <Form.Item
                              name={["existing_contacts", idx, "main_phone"]}
                              label={t("contacts.field.main_phone", "Teléfono Principal")}
                              initialValue={mainPhone.number}
                            >
                              <Input
                                addonBefore={
                                  <Form.Item name={["existing_contacts", idx, "main_phone_prefix"]} noStyle initialValue={mainPhone.prefix}>
                                    <Select style={{ width: 100 }} options={PHONE_PREFIXES} showSearch filterOption={prefixFilter} />
                                  </Form.Item>
                                }
                              />
                            </Form.Item>
                          </Col>
                        </Row>
                        <Row gutter={16}>
                          <Col xs={24} sm={12}>
                            <Form.Item
                              name={["existing_contacts", idx, "secondary_phone"]}
                              label={t("contacts.field.secondary_phone", "Teléfono Secundario")}
                              initialValue={secPhone.number}
                            >
                              <Input
                                addonBefore={
                                  <Form.Item name={["existing_contacts", idx, "secondary_phone_prefix"]} noStyle initialValue={secPhone.prefix}>
                                    <Select style={{ width: 100 }} options={PHONE_PREFIXES} showSearch filterOption={prefixFilter} />
                                  </Form.Item>
                                }
                              />
                            </Form.Item>
                          </Col>
                          <Col xs={24} sm={12}>
                            <Form.Item
                              name={["existing_contacts", idx, "identification_number"]}
                              label={t("contacts.field.identification_number", "Número de Identificación")}
                              initialValue={c.identification_number}
                            >
                              <Input />
                            </Form.Item>
                          </Col>
                        </Row>
                        <Row gutter={16}>
                          <Col xs={24} sm={12}>
                            <Form.Item
                              name={["existing_contacts", idx, "position_id"]}
                              label={t("contacts.field.position", "Cargo/Posición")}
                              initialValue={pc.position?.id}
                            >
                              <Select
                                {...positionSelectProps}
                                placeholder={t("contacts.placeholder.position", "Seleccione un cargo")}
                                allowClear
                                showSearch
                                filterOption={selectFilter}
                              />
                            </Form.Item>
                          </Col>
                        </Row>
                      </>
                    ),
                  };
                })}
            />
          )}
          <ContactFormList />
        </Card>
      </Form>

      {/* Modal: Crear Responsable */}
      <Modal
        title={t("producers.actions.new_agent", "Nuevo responsable")}
        open={agentModalOpen}
        onOk={handleCreateAgent}
        onCancel={() => { setAgentModalOpen(false); agentForm.resetFields(); }}
        confirmLoading={false}
        okText={t("common.save", "Guardar")}
        cancelText={t("common.cancel", "Cancelar")}
      >
        <Form form={agentForm} layout="vertical">
          <Form.Item name="name" label={t("common.name", "Nombre")} rules={[{ required: true, message: t("common.required", "Campo obligatorio") }]}>
            <Input placeholder={t("producers.placeholders.agent_name", "Nombre del responsable")} />
          </Form.Item>
          <Form.Item name="email" label={t("common.email", "Email")} rules={[{ required: true, type: "email", message: t("common.email_invalid", "Email no válido") }]}>
            <Input prefix={<MailOutlined style={{ color: "#bfbfbf" }} />} placeholder={t("producers.placeholders.agent_email", "email@ejemplo.com")} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal: Crear Grupo Empresarial */}
      <Modal
        title={t("producers.actions.new_business_group", "Nuevo grupo empresarial")}
        open={bgModalOpen}
        onOk={handleCreateBg}
        onCancel={() => { setBgModalOpen(false); bgForm.resetFields(); }}
        confirmLoading={false}
        okText={t("common.save", "Guardar")}
        cancelText={t("common.cancel", "Cancelar")}
      >
        <Form form={bgForm} layout="vertical">
          <Form.Item name="name" label={t("common.name", "Nombre")} rules={[{ required: true, message: t("common.required", "Campo obligatorio") }]}>
            <Input placeholder={t("producers.placeholders.bg_name", "Nombre del grupo empresarial")} />
          </Form.Item>
          <Form.Item name="country" label={t("producers.fields.country", "País")} rules={[{ required: true, message: t("common.required", "Campo obligatorio") }]}>
            <Select
              {...countrySelectProps}
              placeholder={t("producers.placeholders.country", "Seleccionar país")}
              allowClear
              showSearch
              filterOption={selectFilter}
            />
          </Form.Item>
        </Form>
      </Modal>
    </Edit>
  );
};
